import React, { useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay/hooks';

import List, { ListProps } from '@material-ui/core/List';
import { graphql } from 'babel-plugin-relay/macro';
import { isEmpty, map } from 'lodash';
import { squigglyTemplates as utilsSquigglies } from 'ui-utils/dist/getters/constants/squigglyTemplates';

import { RolloutEnum } from '@lib/resources/types';

import { EmptyStateList, ListItemSquiggly } from 'components/v2';
import usePermissions from 'hooks/usePermissions';

import {
  QueryListSquiggliesQuery,
  QueryListSquiggliesQueryVariables,
} from './__generated__/QueryListSquiggliesQuery.graphql';
import { useFilterSquigglies } from './hooks/useFilterSquigglies';

export interface QueryListSquiggliesProps extends ListProps {
  squigglies: keyof typeof utilsSquigglies | string[];
  query?: string;
  onSquigglySelected?: (squiggly: any) => void;
  variables?: QueryListSquiggliesQueryVariables;
}

const QueryListSquigglies: React.FC<QueryListSquiggliesProps> = ({
  squigglies,
  query,
  onSquigglySelected,
  variables,
  ...rest
}) => {
  const { viewer, patientFormTemplateMany, lumabotFlowTemplateMany } = useLazyLoadQuery<QueryListSquiggliesQuery>(
    graphql`
      query QueryListSquiggliesQuery(
        $regex: String = ""
        $includePatientFormTemplates: Boolean = false
        $includeLumabotFlowTemplates: Boolean = false
      ) {
        viewer {
          salesforceData {
            provisioning
          }
        }
        patientFormTemplateMany(
          limit: 500
          sort: TITLE_ASC
          filter: {
            AND: [{ active: true, _operators: { lumabot: { nin: [true] } } }]
            OR: [{ _operators: { tag: { regex: $regex } } }, { _operators: { title: { regex: $regex } } }]
          }
        ) @include(if: $includePatientFormTemplates) {
          tag
          title
        }
        lumabotFlowTemplateMany(
          limit: 500
          sort: TITLE_ASC
          filter: {
            active: true
            OR: [{ _operators: { tag: { regex: $regex } } }, { _operators: { title: { regex: $regex } } }]
          }
        ) @include(if: $includeLumabotFlowTemplates) {
          tag
          title
        }
      }
    `,
    variables,
    { fetchPolicy: 'store-or-network' },
  );

  const { hasAccessToProduct } = usePermissions();

  const isLumaBotV1 = hasAccessToProduct(RolloutEnum.LumaBotV1);
  const hasAccessToLumabotSquiggles = hasAccessToProduct(RolloutEnum.LumaBotSquigglies) && !isLumaBotV1;

  const patientFormTemplates = map(patientFormTemplateMany, (patientFormTemplate) => ({
    id: patientFormTemplate?.tag || '',
    name: patientFormTemplate?.title || '',
  }));

  const lumabotFlowTemplates = useMemo(
    () =>
      hasAccessToLumabotSquiggles
        ? map(lumabotFlowTemplateMany, (lumabotFlowTemplate) => ({
            id: lumabotFlowTemplate?.tag || '',
            name: lumabotFlowTemplate?.title || '',
          }))
        : [],
    [hasAccessToLumabotSquiggles, lumabotFlowTemplateMany],
  );

  const squiggliesList = useFilterSquigglies(squigglies, viewer, query, patientFormTemplates, lumabotFlowTemplates);

  return (
    <List {...rest}>
      {isEmpty(squiggliesList) && isEmpty(patientFormTemplates) && isEmpty(lumabotFlowTemplates) ? (
        <EmptyStateList />
      ) : (
        <>
          {map(squiggliesList, (squiggly) => (
            <ListItemSquiggly
              key={squiggly.id}
              squiggly={squiggly}
              onClick={() => onSquigglySelected && onSquigglySelected(squiggly)}
            />
          ))}
          {map(patientFormTemplates, (squiggly) => (
            <ListItemSquiggly
              key={squiggly.id}
              squiggly={squiggly}
              onClick={() => onSquigglySelected && onSquigglySelected(squiggly)}
            />
          ))}
          {map(lumabotFlowTemplates, (squiggly) => (
            <ListItemSquiggly
              key={squiggly.id}
              squiggly={squiggly}
              onClick={() => onSquigglySelected && onSquigglySelected(squiggly)}
            />
          ))}
        </>
      )}
    </List>
  );
};

export default QueryListSquigglies;
